import { useEffect, useState } from "react";
import { Download, Smartphone, Apple, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Standalone PWA install button + helper dialog.
 * - Captures `beforeinstallprompt` on Chromium browsers and triggers the native UI.
 * - On iOS Safari (no programmatic prompt available) explains the manual
 *   "Share → Add to Home Screen" flow.
 * - Hides entirely once the app is already running in standalone mode.
 */
export const InstallAppButton = () => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  useEffect(() => {
    // Already running as installed PWA?
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) { setInstalled(true); return; }

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

  const handleClick = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch (e) { console.warn("Install prompt failed:", e?.message); }
      setDeferred(null);
      return;
    }
    if (isIOS) { setIosHelpOpen(true); return; }
    setIosHelpOpen(true); // generic fallback dialog with manual instructions
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outline"
        className="border-border bg-foreground/[0.02] hover:bg-foreground/[0.05] h-11 w-full sm:w-auto"
        data-testid="install-app-btn"
      >
        <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Install app
      </Button>

      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              {isIOS ? <Apple className="h-5 w-5" strokeWidth={1.5} /> : <Smartphone className="h-5 w-5" strokeWidth={1.5} />}
              Install Split the ____
            </DialogTitle>
          </DialogHeader>
          {isIOS ? (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="brand-text font-mono text-xs mt-0.5">1.</span>
                <span>Tap the <Share className="inline h-3.5 w-3.5 mx-0.5" strokeWidth={1.5} /> <strong>Share</strong> button at the bottom of Safari.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="brand-text font-mono text-xs mt-0.5">2.</span>
                <span>Scroll down and choose <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="brand-text font-mono text-xs mt-0.5">3.</span>
                <span>Tap <strong>Add</strong> — the app icon will appear on your home screen.</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="brand-text font-mono text-xs mt-0.5">1.</span>
                <span>Open the browser menu (three dots).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="brand-text font-mono text-xs mt-0.5">2.</span>
                <span>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="brand-text font-mono text-xs mt-0.5">3.</span>
                <span>Confirm — it opens like a native app, with no browser UI.</span>
              </li>
            </ol>
          )}
          <p className="text-xs text-muted-foreground border-t border-border pt-3 mt-2">
            No App Store account, no download — works offline once installed.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
